import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { del, get, post, put } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import useMessageRealtime from "../../hooks/useMessageRealtime"
import usePresence from "../../hooks/usePresence"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { useChatDock } from "./ChatDockProvider"
import {
  ALLOWED_MESSAGE_MEDIA_TYPES,
  MESSAGE_MEDIA_ACCEPT,
  MESSAGE_MEDIA_ATTACH_TOOLTIP,
  MESSAGE_MEDIA_UNSUPPORTED_MSG,
  QUICK_REACTIONS,
  mapDirectChatMessage,
  mapReactionSummary,
  mergeDirectChatMessages,
  normalizeEmoji,
  replaceReactionCounts,
  toDirectChatMediaPayload,
  uploadDirectChatFiles,
} from "./directChatMessages"

const TOAST_OPTIONS = { autoClose: 4000 }

const getRows = result => {
  const rows = result?.data || result?.messages || result || []
  return Array.isArray(rows) ? rows : []
}

const hasOlderPage = result => {
  const current = Number(
    result?.current_page || result?.meta?.current_page || 1
  )
  const last = Number(result?.last_page || result?.meta?.last_page || current)
  return current < last
}

const DirectChatWindow = ({ contact }) => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const { isOnline } = usePresence()
  const { closeChat, expandChat, minimizeChat } = useChatDock()
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [draft, setDraft] = useState("")
  const [medias, setMedias] = useState([])
  const [composerError, setComposerError] = useState("")
  const [isUploading, setIsUploading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState("")
  const [openMenuId, setOpenMenuId] = useState(null)
  const [openReactions, setOpenReactions] = useState(null)
  const [reactingMessageId, setReactingMessageId] = useState(null)
  const menuRef = useRef(null)
  const editInputRef = useRef(null)
  const attachInputRef = useRef(null)
  const threadRef = useRef(null)
  const previousHeightRef = useRef(null)
  const contextId = contact.id
  const contactOnline = Boolean(contact.userId && isOnline(contact.userId))
  const replyAvatarName =
    [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ") ||
    currentUser?.email ||
    "You"

  const ingest = useCallback(
    raw => {
      const mapped = mapDirectChatMessage(raw, currentUser)
      if (mapped.id)
        setMessages(current => mergeDirectChatMessages(current, [mapped]))
    },
    [currentUser]
  )

  const {
    typingLabel,
    notifyTyping,
    notifyMessageChanged,
    notifyMessageDeleted,
    notifyReaction,
  } = useMessageRealtime({
    contextId,
    projectId: contact.projectId,
    currentUser,
    // Keep subscribed while minimized so inbound messages still update state.
    enabled: Boolean(contextId),
    onMessage: ingest,
    onMessageUpdated: ingest,
    onMessageDeleted: id => {
      setMessages(current =>
        current.filter(message => String(message.id) !== String(id))
      )
    },
    onReaction: ({ messageId, reaction, action, reactionSummary, userId }) => {
      if (!messageId) return
      const removed = String(action || "").includes("remov")
      const actorId = String(userId || "")
      const currentUserId = String(currentUser?.id || "")
      const isActor =
        Boolean(actorId && currentUserId) && actorId === currentUserId
      const summaryReactions = mapReactionSummary(reactionSummary)
      const hasSummary =
        Array.isArray(summaryReactions) && summaryReactions.length > 0

      setMessages(current =>
        current.map(message => {
          if (String(message.id) !== String(messageId)) return message

          const mineMatches =
            normalizeEmoji(message.myReaction) === normalizeEmoji(reaction)
          // Remove payloads still include the emoji at the pre-remove count,
          // so never replace the listing from reaction_summary on remove.
          // Skip a second decrement when this tab already applied locally.
          const alreadyRemovedLocally = removed && isActor && !mineMatches
          let reactions = message.reactions
          if (removed) {
            if (!alreadyRemovedLocally) {
              reactions = replaceReactionCounts(
                message.reactions,
                reaction,
                null
              )
            }
          } else if (hasSummary) {
            reactions = summaryReactions
          } else {
            // Empty summary must not wipe chips — increment locally like project chat.
            reactions = replaceReactionCounts(
              message.reactions,
              null,
              reaction
            )
          }

          let myReaction = message.myReaction
          const stillListed = reactions.some(
            item => normalizeEmoji(item.emoji) === normalizeEmoji(reaction)
          )

          if (isActor) {
            if (removed) {
              myReaction = mineMatches ? "" : message.myReaction
            } else {
              myReaction = stillListed ? reaction : ""
            }
          } else if (removed && mineMatches && !stillListed) {
            myReaction = ""
          }

          return {
            ...message,
            reactions,
            myReaction,
          }
        })
      )
    },
  })

  useEffect(() => {
    if (!openMenuId) return undefined
    const close = event => {
      if (menuRef.current?.contains(event.target)) return
      setOpenMenuId(null)
    }
    const onKey = event => {
      if (event.key === "Escape") setOpenMenuId(null)
    }
    document.addEventListener("mousedown", close)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", close)
      document.removeEventListener("keydown", onKey)
    }
  }, [openMenuId])

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setMessages([])
    get(endpoints.getContextMessages(contextId), {
      params: { page: 1 },
      skipErrorToast: true,
    })
      .then(response => {
        if (!active || !response?.data?.success) return
        const result = response.data.result || {}
        setMessages(
          mergeDirectChatMessages(
            [],
            getRows(result).map(item => mapDirectChatMessage(item, currentUser))
          )
        )
        setHasMore(hasOlderPage(result))
        setPage(1)
      })
      .catch(error => {
        if (active) toastApiError(error, "Failed to load chat")
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [contextId, currentUser])

  useLayoutEffect(() => {
    // Minimized view unmounts the thread — scroll to latest whenever we
    // expand again or messages change (unless restoring after load-older).
    if (contact.minimized || isLoading) return
    const thread = threadRef.current
    if (!thread) return

    if (previousHeightRef.current != null) {
      thread.scrollTop = thread.scrollHeight - previousHeightRef.current
      previousHeightRef.current = null
      return undefined
    }

    const scrollToLatest = () => {
      thread.scrollTop = thread.scrollHeight
    }
    scrollToLatest()
    // Second pass after layout settles (expand remount / images).
    const raf = window.requestAnimationFrame(scrollToLatest)
    return () => window.cancelAnimationFrame(raf)
  }, [messages, contact.minimized, isLoading])

  const loadOlder = async () => {
    if (!hasMore || isLoadingOlder) return
    const thread = threadRef.current
    previousHeightRef.current = thread
      ? thread.scrollHeight - thread.scrollTop
      : null
    setIsLoadingOlder(true)
    try {
      const nextPage = page + 1
      const response = await get(endpoints.getContextMessages(contextId), {
        params: { page: nextPage },
        skipErrorToast: true,
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const older = getRows(result).map(item =>
          mapDirectChatMessage(item, currentUser)
        )
        setMessages(current => mergeDirectChatMessages(older, current))
        setPage(nextPage)
        setHasMore(hasOlderPage(result))
      }
    } catch (error) {
      toastApiError(error, "Failed to load older messages")
    } finally {
      setIsLoadingOlder(false)
    }
  }

  const handleScroll = event => {
    if (event.currentTarget.scrollTop < 24) loadOlder()
  }

  const handleFiles = async event => {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (!files.length || isSending) return

    const invalid = files.filter(
      file => !ALLOWED_MESSAGE_MEDIA_TYPES.includes(file.type)
    )
    if (invalid.length > 0) {
      toast.error(MESSAGE_MEDIA_UNSUPPORTED_MSG, TOAST_OPTIONS)
      return
    }

    setIsUploading(true)
    try {
      const { uploaded } = await uploadDirectChatFiles(files)
      setMedias(current => [...current, ...uploaded])
    } catch (error) {
      toastApiError(error?.payload || error, "Failed to upload media")
    } finally {
      setIsUploading(false)
    }
  }

  const handleSend = async () => {
    if (isSending || isUploading || editingId) return

    const text = draft.trim()
    if (!text) {
      setComposerError("Message is required.")
      return
    }

    setComposerError("")
    const localId = `local-${Date.now()}`
    const sentAt = new Date().toISOString()
    const local = {
      id: localId,
      senderId: String(currentUser?.id || ""),
      sender: "You",
      avatar: getUserAvatarUrl(currentUser),
      text,
      medias,
      reactions: [],
      sentAt,
      time: new Date(sentAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      }),
      isMe: true,
    }
    setMessages(current => mergeDirectChatMessages(current, [local]))
    setDraft("")
    setMedias([])
    notifyTyping(false)
    setIsSending(true)
    try {
      const response = await post(
        endpoints.postContextMessage(contextId),
        {
          message: text,
          parent_message_id: null,
          medias: toDirectChatMediaPayload(medias),
        },
        { skipErrorToast: true }
      )
      if (!response?.data?.success) throw new Error("Failed to send message")
      const created =
        response.data.result?.message ||
        response.data.result?.data ||
        response.data.result
      setMessages(current => current.filter(message => message.id !== localId))
      if (created) {
        ingest(created)
        notifyMessageChanged(created)
      }
    } catch (error) {
      setMessages(current => current.filter(message => message.id !== localId))
      setDraft(text)
      setMedias(medias)
      toastApiError(error, "Failed to send message")
    } finally {
      setIsSending(false)
    }
  }

  const handleStartEdit = message => {
    if (!message?.id || !message.isMe) return
    if (String(message.id).startsWith("local-")) return
    setOpenMenuId(null)
    setOpenReactions(null)
    setEditingId(message.id)
    setEditDraft(message.text || "")
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditDraft("")
    setIsSavingEdit(false)
  }

  useEffect(() => {
    if (!editingId) return undefined
    const frame = window.requestAnimationFrame(() => {
      editInputRef.current?.focus?.()
      editInputRef.current?.select?.()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [editingId])

  const handleSaveEdit = async () => {
    if (!editingId || isSavingEdit) return
    const nextText = editDraft.trim()
    if (!nextText) return

    setIsSavingEdit(true)
    try {
      const response = await put(
        endpoints.updateMessage(editingId),
        { message: nextText, remove_media_ids: [], medias: [] },
        { skipErrorToast: true }
      )
      if (!response?.data?.success) throw new Error("Failed to update message")
      setMessages(current =>
        current.map(message =>
          String(message.id) === String(editingId)
            ? { ...message, text: nextText }
            : message
        )
      )
      notifyMessageChanged(
        response.data.result || { id: editingId, message: nextText }
      )
      handleCancelEdit()
    } catch (error) {
      toastApiError(error, "Failed to update message")
      setIsSavingEdit(false)
    }
  }

  const handleDelete = async message => {
    try {
      const response = await del(endpoints.deleteMessage(message.id), {
        skipErrorToast: true,
      })
      if (!response?.data?.success) throw new Error("Failed to delete message")
      setMessages(current => current.filter(item => item.id !== message.id))
      notifyMessageDeleted(message.id)
    } catch (error) {
      toastApiError(error, "Failed to delete message")
    }
  }

  const handleReaction = async (message, reaction) => {
    if (!message?.id || !reaction || reactingMessageId) return
    const remove =
      normalizeEmoji(message.myReaction) === normalizeEmoji(reaction)
    setOpenReactions(null)
    setReactingMessageId(message.id)
    try {
      const response = remove
        ? await del(endpoints.deleteMessageReaction(message.id), {
            data: { reaction },
            skipErrorToast: true,
          })
        : await post(
            endpoints.postMessageReaction(message.id),
            { reaction },
            { skipErrorToast: true }
          )
      if (!response?.data?.success) throw new Error("Failed to update reaction")

      setMessages(current =>
        current.map(item => {
          if (String(item.id) !== String(message.id)) return item
          if (remove) {
            return {
              ...item,
              reactions: replaceReactionCounts(item.reactions, reaction, null),
              myReaction:
                normalizeEmoji(item.myReaction) === normalizeEmoji(reaction)
                  ? ""
                  : item.myReaction,
            }
          }
          if (normalizeEmoji(item.myReaction) === normalizeEmoji(reaction)) {
            return item
          }
          return {
            ...item,
            reactions: replaceReactionCounts(
              item.reactions,
              item.myReaction,
              reaction
            ),
            myReaction: reaction,
          }
        })
      )

      notifyReaction({
        messageId: message.id,
        reaction,
        action: remove ? "removed" : "added",
      })
    } catch (error) {
      toastApiError(error, "Failed to update reaction")
    } finally {
      setReactingMessageId(null)
    }
  }

  const header = (
    <div className="direct-chat__header">
      <button
        type="button"
        className="direct-chat__identity"
        onClick={() => {
          if (contact.minimized) expandChat(contextId)
        }}
      >
        <span className="direct-chat__avatar" aria-hidden="true">
          {contact.avatar ? (
            <img src={contact.avatar} alt="" />
          ) : (
            contact.initials
          )}
        </span>
        <span className="direct-chat__heading">
          <strong className="direct-chat__name">{contact.name}</strong>
          {contact.userId ? (
            <span
              className={`direct-chat__presence${
                contactOnline
                  ? " direct-chat__presence--online"
                  : " direct-chat__presence--offline"
              }`}
            >
              <span className="direct-chat__presence-dot" aria-hidden="true" />
              {contactOnline ? "Online" : "Offline"}
            </span>
          ) : contact.projectName ? (
            <small className="direct-chat__project">{contact.projectName}</small>
          ) : null}
        </span>
      </button>
      <div className="direct-chat__header-actions">
        <button
          type="button"
          className="direct-chat__icon-btn"
          onClick={() =>
            contact.minimized
              ? expandChat(contextId)
              : minimizeChat(contextId)
          }
          aria-label={contact.minimized ? "Expand chat" : "Minimize chat"}
          title={contact.minimized ? "Expand" : "Minimize"}
        >
          <img src={ICONS.zoomOut} alt="" width={16} height={16} />
        </button>
        <button
          type="button"
          className="direct-chat__close-btn"
          onClick={() => closeChat(contextId)}
          aria-label="Close chat"
          title="Close"
        >
          <img src={ICONS.removeCircle} alt="" width={16} height={16} />
        </button>
      </div>
    </div>
  )

  if (contact.minimized) {
    return (
      <section className="direct-chat direct-chat--minimized">{header}</section>
    )
  }

  return (
    <section className="direct-chat">
      {header}
      <div
        className="direct-chat__messages"
        ref={threadRef}
        onScroll={handleScroll}
      >
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="direct-chat__skeleton amok-skeleton"
              />
            ))
          : null}
        {isLoadingOlder ? (
          <div className="direct-chat__loading">Loading…</div>
        ) : null}
        {!isLoading && messages.length === 0 ? (
          <div className="direct-chat__empty">Start the conversation</div>
        ) : null}
        {messages.map(message => {
          const isOwn = Boolean(message.isMe)
          const canManage =
            isOwn && !String(message.id).startsWith("local-")
          const menuOpen = String(openMenuId) === String(message.id)
          const isEditing = String(editingId) === String(message.id)
          const avatarSrc =
            message.avatar ||
            (isOwn ? getUserAvatarUrl(currentUser) : "") ||
            ""

          return (
            <div
              key={message.id}
              className={`direct-chat__message${
                isOwn ? " direct-chat__message--me" : ""
              }`}
            >
              {!isOwn ? (
                <Avatar
                  src={avatarSrc}
                  name={message.sender}
                  size="sm"
                  className="direct-chat__message-avatar"
                />
              ) : null}

              <div className="direct-chat__message-content">
                <div className="direct-chat__message-meta">
                  {isOwn ? (
                    <Avatar
                      src={avatarSrc}
                      name={message.sender || "You"}
                      size="sm"
                      className="direct-chat__message-avatar"
                    />
                  ) : null}
                  {isOwn ? (
                    <span className="direct-chat__message-you">You</span>
                  ) : (
                    <span className="direct-chat__message-sender">
                      {message.sender}
                    </span>
                  )}
                  {message.time ? (
                    <>
                      <span
                        className="direct-chat__message-dot"
                        aria-hidden="true"
                      >
                        •
                      </span>
                      <span className="direct-chat__message-time">
                        {message.time}
                      </span>
                    </>
                  ) : null}
                  {canManage ? (
                    <div
                      className="direct-chat__msg-menu"
                      ref={menuOpen ? menuRef : null}
                    >
                      <button
                        type="button"
                        className={`direct-chat__msg-menu-trigger${
                          menuOpen
                            ? " direct-chat__msg-menu-trigger--open"
                            : ""
                        }`}
                        aria-label="Message actions"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        onClick={event => {
                          event.stopPropagation()
                          setOpenMenuId(menuOpen ? null : message.id)
                          setOpenReactions(null)
                        }}
                      >
                        <span
                          className="direct-chat__msg-menu-dots"
                          aria-hidden="true"
                        >
                          <span />
                          <span />
                          <span />
                        </span>
                      </button>
                      {menuOpen ? (
                        <div
                          className="direct-chat__msg-menu-dropdown"
                          role="menu"
                        >
                          <button
                            type="button"
                            className="direct-chat__msg-menu-item"
                            role="menuitem"
                            onClick={event => {
                              event.stopPropagation()
                              handleStartEdit(message)
                            }}
                          >
                            <img
                              src={ICONS.edit}
                              alt=""
                              width={14}
                              height={14}
                            />
                            <span>Edit</span>
                          </button>
                          <button
                            type="button"
                            className="direct-chat__msg-menu-item direct-chat__msg-menu-item--danger"
                            role="menuitem"
                            onClick={event => {
                              event.stopPropagation()
                              setOpenMenuId(null)
                              handleDelete(message)
                            }}
                          >
                            <img
                              src={ICONS.trash}
                              alt=""
                              width={14}
                              height={14}
                            />
                            <span>Delete</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {isEditing ? (
                  <div className="direct-chat__edit">
                    <input
                      ref={editInputRef}
                      type="text"
                      className="direct-chat__edit-input"
                      value={editDraft}
                      onChange={event => setEditDraft(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault()
                          handleSaveEdit()
                        }
                        if (event.key === "Escape") {
                          event.preventDefault()
                          handleCancelEdit()
                        }
                      }}
                      disabled={isSavingEdit}
                      aria-label="Edit message"
                    />
                    <div className="direct-chat__edit-actions">
                      <button
                        type="button"
                        className="direct-chat__edit-cancel"
                        onClick={handleCancelEdit}
                        disabled={isSavingEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="direct-chat__edit-save"
                        onClick={handleSaveEdit}
                        disabled={isSavingEdit || !editDraft.trim()}
                      >
                        {isSavingEdit ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="direct-chat__message-text">{message.text}</p>
                )}

                {message.medias?.length ? (
                  <div className="direct-chat__attachments">
                    {message.medias.map(media =>
                      media.isImage ? (
                        <a
                          key={media.id}
                          href={media.url}
                          target="_blank"
                          rel="noreferrer"
                          className="direct-chat__attachment"
                        >
                          <img src={media.url} alt={media.name} />
                        </a>
                      ) : (
                        <a
                          key={media.id}
                          href={media.url}
                          target="_blank"
                          rel="noreferrer"
                          className="direct-chat__attachment-file"
                        >
                          {media.name}
                        </a>
                      )
                    )}
                  </div>
                ) : null}

                <div className="direct-chat__reactions">
                  {(message.reactions || []).map(reaction => (
                    <button
                      key={`${reaction.emoji}-${reaction.count}`}
                      type="button"
                      className={`direct-chat__reaction${
                        normalizeEmoji(message.myReaction) ===
                        normalizeEmoji(reaction.emoji)
                          ? " direct-chat__reaction--mine"
                          : ""
                      }`}
                      onClick={() => handleReaction(message, reaction.emoji)}
                      disabled={Boolean(reactingMessageId)}
                      aria-pressed={
                        normalizeEmoji(message.myReaction) ===
                        normalizeEmoji(reaction.emoji)
                      }
                    >
                      <span>{reaction.emoji}</span>
                      <span>{reaction.count}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`direct-chat__react-add${
                      openReactions === message.id
                        ? " direct-chat__react-add--open"
                        : ""
                    }`}
                    aria-label="Add reaction"
                    aria-expanded={openReactions === message.id}
                    title="Add Reactions"
                    onClick={() =>
                      setOpenReactions(
                        openReactions === message.id ? null : message.id
                      )
                    }
                  >
                    <img
                      src={ICONS.messageToolbarSmile}
                      alt=""
                      width={16}
                      height={16}
                      aria-hidden="true"
                    />
                  </button>
                  {openReactions === message.id ? (
                    <div className="direct-chat__react-picker" role="listbox">
                      {QUICK_REACTIONS.map(reaction => (
                        <button
                          key={reaction}
                          type="button"
                          onClick={() => handleReaction(message, reaction)}
                        >
                          {reaction}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {typingLabel ? (
        <div className="direct-chat__typing">{typingLabel}</div>
      ) : null}
      {medias.length ? (
        <ul className="direct-chat__reply-attachments">
          {medias.map((media, index) => (
            <li
              key={media.id || media.localId || `${media.url}-${index}`}
              className="direct-chat__reply-attachment"
            >
              <span className="direct-chat__reply-attachment-name">
                {media.name || "Attachment"}
              </span>
              <button
                type="button"
                className="direct-chat__reply-attachment-remove"
                aria-label={`Remove ${media.name || "attachment"}`}
                onClick={() =>
                  setMedias(current =>
                    current.filter((_, itemIndex) => itemIndex !== index)
                  )
                }
                disabled={isSending || isUploading}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="direct-chat__composer">
        <div className="direct-chat__chat-input">
          <Avatar
            src={getUserAvatarUrl(currentUser)}
            name={replyAvatarName}
            size="sm"
            className="direct-chat__input-avatar"
          />
          <div
            className={`direct-chat__input-wrap${
              composerError ? " direct-chat__input-wrap--error" : ""
            }`}
          >
            <button
              type="button"
              className="direct-chat__input-action"
              aria-label={MESSAGE_MEDIA_ATTACH_TOOLTIP}
              title={MESSAGE_MEDIA_ATTACH_TOOLTIP}
              onClick={() => attachInputRef.current?.click()}
              disabled={isSending || isUploading}
            >
              <img src={ICONS.messageExport} alt="" width={16} height={16} />
            </button>
            <input
              ref={attachInputRef}
              type="file"
              multiple
              accept={MESSAGE_MEDIA_ACCEPT}
              className="direct-chat__attach-input"
              onChange={handleFiles}
            />
            <input
              type="text"
              value={draft}
              placeholder="Reply to message..."
              aria-label="Reply to message"
              aria-invalid={composerError ? "true" : undefined}
              disabled={isSending}
              onChange={event => {
                setDraft(event.target.value)
                if (composerError) setComposerError("")
                notifyTyping(Boolean(event.target.value.trim()))
              }}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault()
                  handleSend()
                }
              }}
            />
            <button
              type="button"
              className="direct-chat__input-action direct-chat__input-action--send"
              aria-label="Send reply"
              onClick={handleSend}
              disabled={
                isSending ||
                isUploading ||
                (!draft.trim() && !medias.length)
              }
            >
              <img src={ICONS.send} alt="" width={16} height={16} />
            </button>
          </div>
        </div>
        {composerError ? (
          <p className="direct-chat__composer-error" role="alert">
            {composerError}
          </p>
        ) : null}
      </div>
    </section>
  )
}

DirectChatWindow.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    projectName: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    initials: PropTypes.string,
    avatar: PropTypes.string,
    minimized: PropTypes.bool,
  }).isRequired,
}

export default DirectChatWindow
